import { memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Edit, Trash2, Ban, ChevronLeft, ChevronRight } from "lucide-react";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { TagBadge } from "@/features/tags";
import type { TagResponseDTO } from "@/features/tags";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import type {
  GraduationPaymentMethod,
  GraduationResponseDTO,
} from "../types/graduation.types";

const PAYMENT_METHOD_LABEL: Record<GraduationPaymentMethod, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

interface GraduationsTableProps {
  graduations: GraduationResponseDTO[];
  tagsLookup: Map<number, TagResponseDTO>;
  clientsLookup: Map<number, ClientResponseDTO>;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (graduation: GraduationResponseDTO) => void;
  onCancel: (graduation: GraduationResponseDTO) => void;
  onDelete: (graduation: GraduationResponseDTO) => void;
  onPageChange: (page: number) => void;
}

/**
 * Tabela paginada de formaturas com ações por linha (editar, cancelar,
 * excluir). As tags são exibidas via `TagBadge` resolvendo nomes pelo
 * `tagsLookup` que a página injeta após carregar as tags em massa.
 *
 * Para vendedores, exibe apenas a contagem — a lista completa fica no
 * dialog de edição.
 *
 * @example
 * <GraduationsTable
 *   graduations={list.data}
 *   tagsLookup={tagsLookup}
 *   loading={list.loading}
 *   currentPage={list.page}
 *   totalPages={list.totalPages}
 *   onEdit={openEditDialog}
 *   onCancel={openCancelDialog}
 *   onDelete={openDeleteDialog}
 *   onPageChange={list.setPage}
 * />
 */
export const GraduationsTable = memo(function GraduationsTable({
  graduations,
  tagsLookup,
  clientsLookup,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onCancel,
  onDelete,
  onPageChange,
}: GraduationsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Evento</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Vendedores</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {graduations.map((graduation) => (
              <TableRow key={graduation.idGraduation}>
                <TableCell className="font-medium">
                  {graduation.eventName}
                </TableCell>
                <TableCell>{formatDateTime(graduation.eventDate)}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(graduation.totalValue)}
                </TableCell>
                <TableCell>
                  {PAYMENT_METHOD_LABEL[graduation.paymentMethod]}
                </TableCell>
                <TableCell>
                  {graduation.clientId ? (
                    (() => {
                      const client = clientsLookup.get(graduation.clientId);
                      return client ? (
                        <span className="text-sm">{client.name}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          #{graduation.clientId}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {graduation.sellerIds.length}{" "}
                    {graduation.sellerIds.length === 1
                      ? "vendedor"
                      : "vendedores"}
                  </span>
                </TableCell>
                <TableCell>
                  {graduation.tagIds.length === 0 ? (
                    <span className="text-muted-foreground text-sm">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {graduation.tagIds.map((tagId) => {
                        const tag = tagsLookup.get(tagId);
                        if (!tag) {
                          return (
                            <span
                              key={tagId}
                              className="text-xs text-muted-foreground"
                            >
                              #{tagId}
                            </span>
                          );
                        }
                        return <TagBadge key={tagId} tag={tag} size="sm" />;
                      })}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Can permission={ACTIONS.graduations.update}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onEdit(graduation)}
                        title="Editar"
                        aria-label="Editar formatura"
                        disabled={loading}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Can>
                    <Can permission={ACTIONS.graduations.cancel}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onCancel(graduation)}
                        title="Cancelar (também cancela a NF)"
                        aria-label="Cancelar formatura"
                        disabled={loading}
                      >
                        <Ban className="w-4 h-4 text-amber-600" />
                      </Button>
                    </Can>
                    <Can permission={ACTIONS.graduations.delete}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onDelete(graduation)}
                        title="Excluir"
                        aria-label="Excluir formatura"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </Can>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
